import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  CameraAlt as CameraAltIcon,
  PhotoLibrary as PhotoLibraryIcon,
} from '@mui/icons-material';

export const CLINICAL_IMAGE_ACCEPT =
  'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif';

function isLikelyMobileDevice(): boolean {
  if (typeof window === 'undefined') return false;
  if ('ontouchstart' in window) return true;
  return window.matchMedia('(max-width: 900px)').matches;
}

function fileFromCaptureBlob(blob: Blob, prefix = 'foto-clinica'): File {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  return new File([blob], `${prefix}-${stamp}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
}

type ClinicalImageSourceChoiceDialogProps = {
  open: boolean;
  onClose: () => void;
  onGallery: () => void;
  onCamera: () => void;
};

export function ClinicalImageSourceChoiceDialog({
  open,
  onClose,
  onGallery,
  onCamera,
}: ClinicalImageSourceChoiceDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Adicionar imagem</DialogTitle>
      <List disablePadding>
        <ListItemButton onClick={onGallery}>
          <ListItemIcon>
            <PhotoLibraryIcon color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Anexar da galeria ou arquivo"
            secondary="Escolher foto já salva no aparelho ou computador"
          />
        </ListItemButton>
        <ListItemButton onClick={onCamera}>
          <ListItemIcon>
            <CameraAltIcon color="primary" />
          </ListItemIcon>
          <ListItemText
            primary="Tirar foto agora"
            secondary="Abrir a câmera do celular ou webcam"
          />
        </ListItemButton>
      </List>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
      </DialogActions>
    </Dialog>
  );
}

type WebcamPhotoCaptureDialogProps = {
  open: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
};

export function WebcamPhotoCaptureDialog({ open, onClose, onCapture }: WebcamPhotoCaptureDialogProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  useEffect(() => {
    if (!open) {
      stopStream();
      setError(null);
      setStarting(false);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Este navegador não permite acesso à câmera.');
      return;
    }

    let cancelled = false;
    setStarting(true);
    setError(null);

    const start = async () => {
      const attempts: MediaStreamConstraints[] = [
        { video: { facingMode: { ideal: 'environment' } }, audio: false },
        { video: { facingMode: 'user' }, audio: false },
        { video: true, audio: false },
      ];
      let lastErr: unknown;
      for (const constraints of attempts) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          if (cancelled) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            await videoRef.current.play().catch(() => undefined);
          }
          setStarting(false);
          return;
        } catch (err) {
          lastErr = err;
        }
      }
      if (!cancelled) {
        setStarting(false);
        setError(
          lastErr instanceof DOMException && lastErr.name === 'NotAllowedError'
            ? 'Permita o acesso à câmera nas configurações do navegador.'
            : 'Não foi possível abrir a webcam.',
        );
      }
    };

    void start();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [open, stopStream]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        onCapture(fileFromCaptureBlob(blob));
        stopStream();
      },
      'image/jpeg',
      0.92,
    );
  };

  const handleClose = () => {
    stopStream();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Tirar foto</DialogTitle>
      <DialogContent>
        {error ? (
          <Typography color="error" variant="body2" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : (
          <Box
            sx={{
              position: 'relative',
              bgcolor: '#0f172a',
              borderRadius: 2,
              overflow: 'hidden',
              aspectRatio: '4 / 3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {starting ? <CircularProgress sx={{ color: '#fff' }} /> : null}
            <Box
              component="video"
              ref={videoRef}
              autoPlay
              playsInline
              muted
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: starting ? 'none' : 'block',
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={Boolean(error) || starting}
          onClick={handleCapture}
          startIcon={<CameraAltIcon />}
        >
          Capturar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export type UseClinicalImageAttachFlowOptions = {
  onFiles: (files: File[]) => void;
  multiple?: boolean;
};

export function useClinicalImageAttachFlow({
  onFiles,
  multiple = true,
}: UseClinicalImageAttachFlowOptions) {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const onFilesRef = useRef(onFiles);
  onFilesRef.current = onFiles;

  const emitFiles = useCallback((list: FileList | null) => {
    const files = Array.from(list || []).filter(
      (file) =>
        file.type.startsWith('image/')
        || /\.(jpe?g|png|webp|gif|heic|heif)$/i.test(file.name),
    );
    if (files.length) onFilesRef.current(files);
  }, []);

  const openImageSourceChooser = useCallback(() => {
    setChoiceOpen(true);
  }, []);

  const pickGallery = useCallback(() => {
    setChoiceOpen(false);
    galleryInputRef.current?.click();
  }, []);

  const pickCamera = useCallback(() => {
    setChoiceOpen(false);
    if (isLikelyMobileDevice()) {
      cameraInputRef.current?.click();
      return;
    }
    setWebcamOpen(true);
  }, []);

  const imageSourcePickerNodes = (
    <>
      <input
        ref={galleryInputRef}
        type="file"
        hidden
        multiple={multiple}
        accept={CLINICAL_IMAGE_ACCEPT}
        onChange={(e) => {
          emitFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        hidden
        accept="image/*"
        capture="environment"
        onChange={(e) => {
          emitFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <ClinicalImageSourceChoiceDialog
        open={choiceOpen}
        onClose={() => setChoiceOpen(false)}
        onGallery={pickGallery}
        onCamera={pickCamera}
      />
      <WebcamPhotoCaptureDialog
        open={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        onCapture={(file) => {
          onFilesRef.current([file]);
          setWebcamOpen(false);
        }}
      />
    </>
  );

  return { openImageSourceChooser, imageSourcePickerNodes };
}
